import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { Element } from '../../core/types';
import type { Polygon2D } from '../../utils/siteGeometry';
import { polygonArea } from '../../utils/siteGeometry';
import i18n from '../../i18n';

/**
 * Building Pad: sketch the building footprint on the terrain; creates a
 * 'Building Pads' element. The toposolid renderer cuts every pad footprint out
 * of the terrain (excavation) so the pad reads as a leveled void in the ground
 * exactly like BIM's pad-driven toposolid editing.
 */
export class BuildingPadTool extends BaseTool {
    id = 'SITE_BUILDING_PAD';
    name = 'Building Pad';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.buildingPadSketchThePadBoundary'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.createBuildingPad();
            this.reset();
            return;
        }
        this.points.push(snapped);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 3) {
            this.createBuildingPad();
            this.reset();
        } else if (event.key === 'Escape' && this.points.length > 0) {
            this.reset();
        } else {
            super.onKeyDown(event);
        }
    }

    onPointerUp(): void { }

    private reset() {
        this.points = [];
        this.currentPoint = null;
        this.context?.appStore.setToolPreview(null);
    }

    private updatePreview() {
        if (!this.context || this.points.length === 0) return;
        const previewPoints = [...this.points];
        if (this.currentPoint) previewPoints.push(this.currentPoint);
        if (previewPoints.length > 2) previewPoints.push(previewPoints[0].clone());

        this.context.appStore.setToolPreview({
            type: 'polygon',
            points: previewPoints,
            closed: previewPoints.length > 2,
            color: '#9aa4ad',
        });
    }

    private createBuildingPad() {
        if (!this.context || this.points.length < 3) return;
        const boundary: Polygon2D = this.points.map((p) => [p.x, p.y]);
        const props = this.context.bimStore.toolProperties;
        const kind = props.buildingPadKind ?? 'Cut';
        const elevation = props.buildingPadElevation ?? this.points[0].z;

        const pad: Element = {
            id: uuidv4(),
            category: 'Building Pads',
            type: `Building Pad: ${kind}`,
            name: 'Building Pad',
            parameters: {
                'Kind': kind,
                'Pad Elevation': `${elevation.toFixed(2)} m`,
                'Area': polygonArea(boundary).toFixed(2) + ' m²',
            },
            constraints: [],
            geometryData: { boundary, elevation, kind },
        };
        this.context.bimStore.addElement(pad);
        this.notify(i18n.t('tools.notify.buildingPadCreated', { kind: kind }), 'success');
    }
}
